import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/lib/supabase';
import type { UserProfile, UserRole } from '@/types/userProfile';
import type { Employee } from '@/types/employee';
import type { AccessSummaryGroup } from '@/types/permissions';

// ─────────────────────────────────────────────
// LIST : tous les user_profiles, joints aux employees
// ─────────────────────────────────────────────

export interface UserWithEmployee {
  profile: UserProfile;
  employee: Employee | null;
}

export function useUsers(options?: { enabled?: boolean }) {
  return useQuery({
    queryKey: ['users'],
    enabled: options?.enabled ?? true,
    queryFn: async (): Promise<UserWithEmployee[]> => {
      const { data: profiles, error } = await supabase
        .from('user_profiles')
        .select('*')
        .order('role', { ascending: true });
      if (error) throw error;

      const profilesList = (profiles ?? []) as UserProfile[];
      if (profilesList.length === 0) return [];

      const employeeIds = profilesList
        .map((p) => p.employee_id)
        .filter((id): id is string => !!id);

      let employees: Employee[] = [];
      if (employeeIds.length > 0) {
        const { data: empData, error: empError } = await supabase
          .from('employees')
          .select('*')
          .in('id', employeeIds);
        if (empError) throw empError;
        employees = (empData ?? []) as Employee[];
      }

      return profilesList.map((profile) => ({
        profile,
        employee: profile.employee_id
          ? employees.find((e) => e.id === profile.employee_id) ?? null
          : null,
      }));
    },
  });
}

// ─────────────────────────────────────────────
// PROVISION : créer un compte pour un employé
// (appelle l'Edge Function provision-employee-account)
// ─────────────────────────────────────────────

interface ProvisionPayload {
  employee_id: string;
  email: string;
  password: string;
  role: UserRole;
}

interface ProvisionResponse {
  success: boolean;
  user_id?: string;
  email?: string;
  employee_name?: string;
  role?: UserRole;
  error?: string;
}

/**
 * Extrait le VRAI message d'erreur d'une Edge Function. `functions.invoke`
 * renvoie un message générique (« Edge Function returned a non-2xx status
 * code ») ; le détail JSON ({ error }) est dans `error.context` (la réponse
 * HTTP). On le lit pour afficher la vraie raison à l'utilisateur.
 */
async function functionErrorMessage(
  error: unknown,
  fallback: string
): Promise<string> {
  const ctx = (error as { context?: Response } | null)?.context;
  if (ctx && typeof ctx.clone === 'function') {
    try {
      const body = (await ctx.clone().json()) as { error?: string };
      if (body?.error) return body.error;
    } catch {
      /* corps non-JSON — on ignore */
    }
  }
  if (error instanceof Error && error.message) return error.message;
  return fallback;
}

export function useProvisionEmployee() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (payload: ProvisionPayload): Promise<ProvisionResponse> => {
      const { data, error } = await supabase.functions.invoke(
        'provision-employee-account',
        { body: payload }
      );

      if (error) {
        throw new Error(
          await functionErrorMessage(error, 'Erreur lors de la création')
        );
      }
      if (data?.error) {
        throw new Error(data.error);
      }

      return data as ProvisionResponse;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['users'] });
    },
  });
}

// ─────────────────────────────────────────────
// LINK : lier un compte EXISTANT (client) à un employé
// (courriel déjà pris → convertit le compte client en compte staff)
// ─────────────────────────────────────────────

export function useLinkExistingAccount() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (payload: {
      employee_id: string;
      email: string;
      role: UserRole;
    }): Promise<string> => {
      const { data, error } = await supabase.rpc(
        'link_employee_to_existing_account',
        {
          p_employee_id: payload.employee_id,
          p_email: payload.email.trim(),
          p_role: payload.role,
        }
      );
      if (error) throw new Error(error.message);
      return data as string;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['users'] });
      queryClient.invalidateQueries({ queryKey: ['employees'] });
    },
  });
}

// ─────────────────────────────────────────────
// UPDATE ROLE : changer le rôle d'un user
// ─────────────────────────────────────────────

export function useUpdateUserRole() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      userId,
      role,
    }: {
      userId: string;
      role: UserRole;
    }) => {
      const { error } = await supabase
        .from('user_profiles')
        .update({ role })
        .eq('id', userId);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['users'] });
    },
  });
}

// ─────────────────────────────────────────────
// RESET PASSWORD : redonne un mdp à un employé
// (appelle l'Edge Function reset-employee-password)
// ─────────────────────────────────────────────

interface ResetPasswordPayload {
  user_id: string;
  password: string;
}

interface ResetPasswordResponse {
  success: boolean;
  error?: string;
}

export function useResetEmployeePassword() {
  return useMutation({
    mutationFn: async (
      payload: ResetPasswordPayload
    ): Promise<ResetPasswordResponse> => {
      const { data, error } = await supabase.functions.invoke(
        'reset-employee-password',
        { body: payload }
      );
      if (error) throw new Error(await functionErrorMessage(error, 'Erreur réseau'));
      if (data?.error) throw new Error(data.error);
      return data as ResetPasswordResponse;
    },
  });
}

// ─────────────────────────────────────────────
// WELCOME EMAIL : courriel de bienvenue au nouvel employé
// (appelle l'Edge Function send-employee-welcome)
// ─────────────────────────────────────────────

interface WelcomeEmailPayload {
  email: string; // identifiant de connexion
  password: string; // mot de passe en clair
  first_name: string;
  role_label: string;
  access_groups: AccessSummaryGroup[];
}

interface WelcomeEmailResponse {
  success: boolean;
  error?: string;
}

export function useSendEmployeeWelcome() {
  return useMutation({
    mutationFn: async (
      payload: WelcomeEmailPayload
    ): Promise<WelcomeEmailResponse> => {
      const { data, error } = await supabase.functions.invoke(
        'send-employee-welcome',
        { body: payload }
      );
      if (error) throw new Error(await functionErrorMessage(error, 'Erreur réseau'));
      if (data?.error) throw new Error(data.error);
      return data as WelcomeEmailResponse;
    },
  });
}
